import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { callWithAuth, putReviewCard, deleteCard } from "@/api/Api";
import { PutCardRequest, DeleteCardRequest, Card } from "@shared_types";
import { REVIEW_CARD_COUNT_QUERY_KEY } from "@/components/Review/ReviewInsights";
import { InfiniteCardStream } from "@/lib/cards";
import { withRetry } from "@/lib/utils";

interface UseReviewCardsProps {
  onEmpty?: () => void;
  onError?: (error: Error) => void;
}

const RETRY_COUNT = 3;
const RETRY_DELAY = 600;

export const useReviewCards = ({
  onEmpty,
  onError,
}: UseReviewCardsProps = {}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const queryClient = useQueryClient();
  const [currentCard, setCurrentCard] = useState<Card | null>(null);

  const cardStream = useRef(InfiniteCardStream.getInstance());
  const once = useRef(false);
  // Fetch cards using React Query

  useEffect(() => {
    const fetchFirstCard = async () => {
      if (!once.current && cardStream.current) {
        once.current = true;
        try {
          const firstCard = await cardStream.current.peek();
          if (firstCard) {
            setCurrentCard(firstCard);
            return;
          }
          const nextCard = await cardStream.current.next();
          if (nextCard) {
            setCurrentCard(nextCard);
          } else {
            handleEmpty();
          }
        } catch (err) {
          console.error("Error fetching first card:", err);
          handleFirstCardError(err as Error);
        }
      }
    };
    fetchFirstCard();
  }, []);

  // Handle card flip
  const handleFlip = () => {
    setIsFlipped(true);
  };

  // Move to the next card, fetch more if needed
  const nextCard = async () => {
    const nextCard = await cardStream.current.next();
    if (nextCard) {
      setIsFlipped(false);
      setCurrentCard(nextCard);
    } else {
      setIsFlipped(false);
      handleEmpty();
    }
  };

  const handleEmpty = () => {
    cardStream.current.reset();
    onEmpty?.();
  };

  const handleFirstCardError = (error: Error) => {
    cardStream.current.reset();
    onError?.(error);
  };

  // Mark card as "good"
  const markGood = async (cardId: number) => {
    try {
      const data: PutCardRequest = {
        cardId,
        action: "good",
      };

      // Use withRetry for API call
      const res = await withRetry(
        () => callWithAuth(putReviewCard, data),
        RETRY_COUNT,
        RETRY_DELAY
      );

      // Still update query cache
      queryClient.invalidateQueries({
        queryKey: [REVIEW_CARD_COUNT_QUERY_KEY],
      });

      return res;
    } catch (err) {
      console.error("Error marking card as good:", err);
      onError?.(err as Error);
      return false;
    }
  };

  // Mark card as "again" with retry
  const markAgain = async (cardId: number) => {
    if (!cardId) return false;

    try {
      const data: PutCardRequest = {
        cardId,
        action: "again",
      };

      // Use withRetry for API call
      const res = await withRetry(
        () => callWithAuth(putReviewCard, data),
        RETRY_COUNT,
        RETRY_DELAY
      );

      queryClient.invalidateQueries({
        queryKey: [REVIEW_CARD_COUNT_QUERY_KEY],
      });

      return res;
    } catch (err) {
      console.error("Error marking card as again:", err);
      onError?.(err as Error);
      return false;
    }
  };

  // Delete the current card with retry
  const deleteCurrentCard = async (cardId: number) => {
    if (!cardId) return false;

    try {
      const data: DeleteCardRequest = {
        cardId,
      };

      // Use withRetry for API call
      await withRetry(
        () => callWithAuth(deleteCard, data),
        RETRY_COUNT,
        RETRY_DELAY
      );

      return true;
    } catch (err) {
      console.error("Error deleting card:", err);
      onError?.(err as Error);
      return false;
    }
  };

  return {
    // Card data
    currentCard,
    isFlipped,

    // Actions
    handleFlip,
    nextCard,
    markGood,
    markAgain,
    deleteCurrentCard,
  };
};
